/**
 * Global Error Boundary Component
 * Catches JavaScript errors anywhere in the component tree and displays a fallback UI
 */

import React from 'react';
import { Box, Typography, Button, Paper, Alert } from '@mui/material';
import { RefreshOutlined, BugReportOutlined } from '@mui/icons-material';
import { toast } from 'react-toastify';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: null
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    const errorId = `error_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    this.setState({
      error,
      errorInfo,
      errorId
    });

    // Log error details
    console.error('Error Boundary caught an error:', error, errorInfo);
    
    // You could send this to an error reporting service
    this.logErrorToService(error, errorInfo, errorId);
    
    // Show error notification
    toast.error(`An unexpected error occurred (${errorId}). Please try refreshing the page.`);
  }

  logErrorToService = (error, errorInfo, errorId) => {
    // In production, you would send this to your error tracking service
    // like Sentry, LogRocket, or Bugsnag
    const errorReport = {
      errorId,
      message: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
      timestamp: new Date().toISOString(),
      url: window.location.href,
      userAgent: navigator.userAgent,
    };

    // For now, just store in localStorage for debugging
    const existingErrors = JSON.parse(localStorage.getItem('error_reports') || '[]');
    existingErrors.push(errorReport);
    
    // Keep only last 10 errors to prevent storage bloat
    if (existingErrors.length > 10) {
      existingErrors.shift();
    }
    
    localStorage.setItem('error_reports', JSON.stringify(existingErrors));
  };

  handleReload = () => {
    window.location.reload();
  };

  handleRetry = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: null
    });
  };

  handleReportBug = () => {
    const { error, errorInfo, errorId } = this.state;
    const errorDetails = {
      errorId,
      message: error?.message || 'Unknown error',
      stack: error?.stack || 'No stack trace available',
      componentStack: errorInfo?.componentStack || 'No component stack available',
      timestamp: new Date().toISOString(),
      url: window.location.href,
    };

    // Create a mailto link with error details
    const subject = encodeURIComponent(`Bug Report: ${errorId}`);
    const body = encodeURIComponent(`
Error Details:
${JSON.stringify(errorDetails, null, 2)}

Please describe what you were doing when this error occurred:
[Your description here]
    `);
    
    window.open(`mailto:support@shieldshot.com?subject=${subject}&body=${body}`);
  };

  render() {
    if (this.state.hasError) {
      const isProduction = process.env.NODE_ENV === 'production';
      
      return (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="100vh"
          padding={3}
          bgcolor="background.default"
        >
          <Paper
            elevation={3}
            sx={{
              padding: 4,
              maxWidth: 600,
              width: '100%',
              textAlign: 'center'
            }}
          >
            <BugReportOutlined 
              sx={{ 
                fontSize: 64, 
                color: 'error.main',
                marginBottom: 2
              }} 
            />
            
            <Typography variant="h4" component="h1" gutterBottom color="error">
              Oops! Something went wrong
            </Typography>
            
            <Typography variant="body1" paragraph color="text.secondary">
              We encountered an unexpected error. Don't worry - your data is safe and the issue has been logged.
            </Typography>

            {this.state.errorId && (
              <Alert severity="info" sx={{ marginBottom: 2 }}>
                Error ID: <code>{this.state.errorId}</code>
              </Alert>
            )}
            
            <Box display="flex" gap={2} justifyContent="center" flexWrap="wrap">
              <Button
                variant="contained"
                color="primary"
                startIcon={<RefreshOutlined />}
                onClick={this.handleRetry}
              >
                Try Again
              </Button>
              
              <Button
                variant="outlined"
                color="primary"
                onClick={this.handleReload}
              >
                Reload Page
              </Button>
              
              <Button
                variant="outlined"
                color="secondary"
                startIcon={<BugReportOutlined />}
                onClick={this.handleReportBug}
              >
                Report Bug
              </Button>
            </Box>

            {!isProduction && this.state.error && (
              <Box
                mt={3}
                p={2}
                bgcolor="grey.100"
                borderRadius={1}
                textAlign="left"
              >
                <Typography variant="subtitle2" gutterBottom>
                  Error Details (Development Mode):
                </Typography>
                <Typography variant="body2" component="pre" sx={{ fontSize: '0.75rem' }}>
                  {this.state.error.toString()}
                  {this.state.errorInfo?.componentStack}
                </Typography>
              </Box>
            )}
          </Paper>
        </Box>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
